"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type LoaderVariant = "spinner" | "dots" | "pulse" | "lottie" | "valuation";
const DEFAULT_LOADING_TEXT = "Loading...";
const DEFAULT_LOADER_VARIANT: LoaderVariant = "lottie";

export interface LoaderOptions {
  text?: string;
  variant?: LoaderVariant;
}

interface LoadingContextType {
  isLoading: boolean;
  loadingText: string;
  loaderVariant: LoaderVariant;
  showLoader: (options?: string | LoaderOptions) => void;
  hideLoader: () => void;
  setLoadingText: (text: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

const getLoaderState = (options?: string | LoaderOptions) => {
  if (typeof options === "string") {
    return {
      text: options || DEFAULT_LOADING_TEXT,
      variant: DEFAULT_LOADER_VARIANT,
    };
  }

  return {
    text: options?.text || DEFAULT_LOADING_TEXT,
    variant: options?.variant || DEFAULT_LOADER_VARIANT,
  };
};

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [loaderVariant, setLoaderVariant] = useState<LoaderVariant>(DEFAULT_LOADER_VARIANT);

  const showLoader = useCallback((options?: string | LoaderOptions) => {
    const nextLoaderState = getLoaderState(options);

    setIsLoading(true);
    setLoaderVariant(nextLoaderState.variant);
    setLoadingText(nextLoaderState.text);
  }, []);

  const hideLoader = useCallback(() => {
    setIsLoading(false);
    setLoadingText(DEFAULT_LOADING_TEXT);
    setLoaderVariant(DEFAULT_LOADER_VARIANT);
  }, []);

  const setLoadingTextHandler = useCallback((text: string) => {
    setLoadingText(text);
  }, []);

  const value: LoadingContextType = {
    isLoading,
    loadingText,
    loaderVariant,
    showLoader,
    hideLoader,
    setLoadingText: setLoadingTextHandler,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};
